/* Bellvari Messenger. Independent openings and retained contents, model-viewer 4.1. */
const defaultRuntime = new URL('./model-viewer.min.js', import.meta.url).href;
const runtimeLoads = new Map();
const localMeshoptDecoder = new URL('./meshopt_decoder.js', import.meta.url).href;
function configureMeshoptDecoder() {
  const Viewer = customElements.get('model-viewer');
  if (!Viewer) return;
  const configured = Viewer.meshoptDecoderLocation;
  // Native Shopify galleries may register the runtime first. Configure its
  // optional meshopt loader too, while preserving any merchant-specific URL.
  if (!configured || /^https:\/\/www\.gstatic\.com\/meshopt\/versioned\/[^?#]+\/meshopt_decoder\.js$/.test(configured)) {
    Viewer.meshoptDecoderLocation = localMeshoptDecoder;
  }
}
function canWarmRuntime() {
  const connection = globalThis.navigator?.connection;
  return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
}
function preloadModel(source, owner, priority = 'high') {
  if (!owner?.head) return null;
  const link = owner.createElement('link');
  // Match model-viewer's normal anonymous CORS fetch so the browser can reuse
  // this response; do not create a second fetch, blob URL or decoded model.
  link.rel = 'preload';
  link.as = 'fetch';
  link.crossOrigin = 'anonymous';
  link.fetchPriority = priority;
  link.href = new URL(source, owner.baseURI).href;
  owner.head.appendChild(link);
  return link;
}

const PALETTES = {'crazy-horse': 'Crazy Horse', black: 'Black'};
const CLIPS = ['Hold', 'FlapOpening', 'MainOpening', 'FrontSlipOpening', 'PackingContents'];
const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const smooth = value => value * value * (3 - 2 * value);
const FEATURES = {
  pockets: {name: 'Front organiser', view: 'pockets', flap: 1, main: 0, slip: 1, description: 'Open the front organiser to see its pouch, card slots and pen loops.'},
  inside: {name: 'Laptop compartment', view: 'inside', flap: 1, main: 1, slip: 0, description: 'Look into the padded sleeve and layered fabric pockets.'},
  zipper: {name: 'Main zipper', view: 'zipper', flap: 1, main: 1, slip: 0, description: 'See the zip opening beneath the flap.'},
  trolley: {name: 'Trolley strap', view: 'back', flap: 0, main: 0, slip: 0, description: 'Turn the bag to inspect the strap on the back.'},
  locks: {name: 'Buckles & catches', view: 'locks', flap: 0, main: 0, slip: 0, description: 'Inspect the adjustable buckles and the catches underneath.'},
  carry: {name: 'Carry handle & strap', view: 'carry', flap: 0, main: 0, slip: 0, description: 'Inspect the top handle and shoulder-strap attachments.'}
};
const DEFAULTS = {
  packingDuration: 12,
  mainHinge: [0, .022, .016],
  mainOpeningAngle: 6,
  mainOpeningStart: 7 / 30,
  frontSlipHinge: [0, .024, .020],
  frontSlipOpeningAngle: 8,
  frontSlipOpeningStart: 7 / 30,
  penMountPosition: [.128, .108, .0325],
  presets: {
    front: {theta: 24, phi: 78, radius: .84, target: [0, .16, 0]},
    pockets: {theta: 5, phi: 12, radius: .80, target: [0, .19, .05]},
    inside: {theta: 25, phi: 34, radius: 1.0, target: [0, .18, .035]},
    zipper: {theta: -25, phi: 40, radius: .78, target: [0, .235, .015]},
    back: {theta: 184, phi: 77, radius: .88, target: [0, .16, 0]},
    locks: {theta: 5, phi: 81, radius: .58, target: [0, .14, .065]},
    carry: {theta: 56, phi: 52, radius: .82, target: [0, .23, 0]},
    packing: {theta: 23, phi: 42, radius: 1.10, target: [0, .25, -.015]}
  },
  packingItems: [
    {start: 0, label: 'Laptop', destination: 'Laptop sleeve', position: [0, .131, -.035], normal: [0, 1, 1], entryTop: .555},
    {start: 2, label: 'Notebook', destination: 'Main compartment', position: [0, .124, .006], normal: [0, 1, 0], followsMain: true, entryTop: .525},
    {start: 4, label: 'Passport', destination: 'Front pocket', position: [-.095, .102, .063], normal: [0, 1, 1], followsMain: true, followsSlip: true, entryTop: .39, entryFront: .43},
    {start: 6, label: 'Phone', destination: 'Front pocket', position: [.095, .103, .063], normal: [0, 1, 1], followsMain: true, followsSlip: true, entryTop: .39, entryFront: .43},
    {start: 8, label: 'Charger', destination: 'Main compartment', position: [.11, .058, .006], normal: [0, 1, 0], followsMain: true, entryTop: .49},
    {start: 10, label: 'Pen', destination: 'Front organiser', position: [.128, .180, .0325], normal: [0, 1, 1], followsMain: true, followsPenMount: true, entryTop: .50, entryFront: .27}
  ],
  packingCameras: [
    [0, {theta: 23, phi: 43, radius: 1.05, target: [0, .265, -.01]}],
    [2, {theta: 23, phi: 38, radius: 1.0, target: [0, .23, -.01]}],
    [3.6, {theta: 12, phi: 56, radius: 1.0, target: [0, .22, .055]}],
    [7.6, {theta: 12, phi: 56, radius: 1.0, target: [0, .22, .055]}],
    [8.4, {theta: 30, phi: 33, radius: 1.0, target: [0, .23, -.01]}],
    [12, {theta: 25, phi: 34, radius: 1.0, target: [0, .18, .035]}]
  ],
  // Body envelope only. The current item adds its own temporary entry clearance.
  packingBounds: [-.26, 0, -.19, .26, .345, .25]
};

async function ensureRuntime(source) {
  if (!customElements.get('model-viewer')) {
    if (!runtimeLoads.has(source)) {
      runtimeLoads.set(source, import(source).then(() => {
        const Viewer = customElements.get('model-viewer');
        if (Viewer?.modelCacheSize === 5) Viewer.modelCacheSize = 2;
        const stockDecoder = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';
        if (Viewer && (!Viewer.dracoDecoderLocation || Viewer.dracoDecoderLocation === stockDecoder)) {
          Viewer.dracoDecoderLocation = new URL('./', source).href;
        }
      }).catch(error => {
        runtimeLoads.delete(source);
        if (!customElements.get('model-viewer')) throw error;
      }));
    }
    await runtimeLoads.get(source);
  }
  await customElements.whenDefined('model-viewer');
  configureMeshoptDecoder();
}

class BellvariMessengerExplorer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      this.watchRuntime();
      this.bindGlobalEvents();
      this.resizeObserver?.observe(this.q('.bvm-stage'));
      if (this.started && !this.ready) void this.loadModel(this.pendingCamera);
      else this.runMotion();
      return;
    }
    const configNode = this.querySelector('script[data-bvm-config]');
    this.config = {...DEFAULTS, presets: {...DEFAULTS.presets}};
    if (configNode) {
      try { this.configure(JSON.parse(configNode.textContent)); } catch { /* Safe defaults for a malformed optional override. */ }
    }
    this.initialized = true;
    this.loading = false;
    this.ready = false;
    this.started = false;
    this.palette = this.paletteAvailable('crazy-horse') ? 'crazy-horse' : this.paletteAvailable('black') ? 'black' : 'crazy-horse';
    this.mode = 'closed';
    this.view = 'front';
    this.poses = {flap: 0, main: 0, slip: 0};
    this.targets = {flap: 0, main: 0, slip: 0};
    this.packingProgress = 0;
    this.contentTarget = 0;
    this.packingGuided = false;
    this.loadRequest = 0;
    this.motionRequest = 0;
    this.cameraRequest = 0;
    const commerce = this.querySelector('[data-bv-explorer-commerce]');
    this.innerHTML = `<div class="bvm-shell"><div class="bvm-layout"><div class="bvm-stage" aria-busy="false">
      <div class="bvm-palettes" role="group" aria-label="Leather colour">${Object.entries(PALETTES).map(([key, label]) => `<button type="button" data-palette="${key}" aria-label="${label}" aria-pressed="false"><span class="bvm-swatch bvm-swatch--${key}" aria-hidden="true"></span></button>`).join('')}</div>
      <div class="bvm-tools" role="group" aria-label="3D view controls"><button type="button" data-tool="out" aria-label="Zoom out">−</button><button type="button" data-tool="in" aria-label="Zoom in">+</button><button type="button" data-tool="reset" aria-label="Reset view">↺</button><button type="button" data-tool="fullscreen" aria-label="Fullscreen view">⛶</button></div>
      <div class="bvm-launch"><picture><source media="(max-width: 800px)"><img alt="" loading="lazy" decoding="async"></picture><button type="button" data-start>Explore in 3D ↗</button></div>
      <div class="bvm-packing-callout" role="status" aria-live="polite" aria-atomic="true" hidden><span class="bvm-packing-item"></span><span class="bvm-packing-destination"></span></div>
      <div class="bvm-controls"><div class="bvm-tabs" role="group" aria-label="Bag view"><button type="button" data-mode="closed" aria-pressed="true">Exterior</button><button type="button" data-mode="open" aria-pressed="false">Look inside</button><button type="button" data-mode="packing" aria-pressed="false">Pack the bag</button></div></div>
      <span class="bvm-hint">Drag to rotate. Pinch or scroll to zoom.</span>
      </div><aside class="bvm-features" aria-label="Explore bag details"><p class="bvm-features-heading">Explore the details</p><div class="bvm-feature-list" role="group" aria-label="Bag features">${Object.entries(FEATURES).map(([key, feature]) => `<button type="button" class="bvm-feature" data-feature="${key}" aria-pressed="false"><span>${feature.name}</span><span aria-hidden="true">↗</span></button>`).join('')}</div><p class="bvm-feature-detail">Choose a detail to take a closer look.</p></aside></div>
      <div class="bvm-support"><p class="bvm-illustration" hidden>Illustrative packing. Items not included.</p><p class="bvm-status" role="status" aria-live="polite" aria-atomic="true"></p></div></div>`;
    this.q = selector => this.querySelector(selector);
    this.qa = selector => this.querySelectorAll(selector);
    if (commerce) this.q('.bvm-features').appendChild(commerce);
    this.q('[data-start]').addEventListener('click', () => { this.revealStage(); void this.start(); });
    this.qa('[data-mode]').forEach(button => button.addEventListener('click', () => void this.setMode(button.dataset.mode)));
    this.qa('[data-feature]').forEach(button => button.addEventListener('click', () => void this.selectFeature(button.dataset.feature)));
    this.qa('[data-palette]').forEach(button => button.addEventListener('click', () => void this.setPalette(button.dataset.palette)));
    this.qa('[data-tool]').forEach(button => button.addEventListener('click', () => this.tool(button.dataset.tool)));
    this.qa('[data-view]').forEach(button => button.addEventListener('click', () => {
      if (button.dataset.view === 'inside') void this.setMode('open');
      else void this.chooseView(button.dataset.view);
    }));
    this.bindGlobalEvents();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.ready && !this.cameraIsCustom) this.applyCamera();
      this.updatePackingLabel();
    });
    this.resizeObserver.observe(this.q('.bvm-stage'));
    this.watchRuntime();
    this.updatePoster();
    this.updateControls();
    // The poster remains visible while the selected model warms in the cache.
  }

  watchRuntime() {
    this.stopRuntimeWarmup();
    if (this.started) return;
    // Warm a single selected finish near the section, without creating a viewer
    // or competing with the above-the-fold gallery on page load.
    this.warmIntent = () => this.warmSelectedModel(true);
    this.addEventListener('pointerenter', this.warmIntent, {passive: true});
    this.addEventListener('focusin', this.warmIntent);
    if (!canWarmRuntime() || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => {
      if (this.runtimeObserver !== observer || !this.isConnected) return;
      this.nearViewport = entries.some(entry => entry.isIntersecting);
      if (this.nearViewport) this.warmSelectedModel();
    }, {rootMargin: '300px 0px', threshold: 0});
    this.runtimeObserver = observer;
    observer.observe(this);
  }

  warmSelectedModel(intent = false) {
    if (this.started || !this.isConnected || !canWarmRuntime() || (!intent && !this.nearViewport)) return;
    const source = this.source('unified-src');
    if (!source) return;
    let href;
    try { href = new URL(source, this.ownerDocument.baseURI).href; }
    catch { return; }
    this.warmedSources ||= new Set();
    // Failed background work is retried only by an explicit launch, never by
    // repeated visibility, focus or hover events.
    if (this.warmedSources.has(href)) return;
    this.warmedSources.add(href);
    this.preloadSelectedModel(source, 'low');
    if (!this.runtimeWarmAttempted) {
      this.runtimeWarmAttempted = true;
      try { void ensureRuntime(this.runtimeSource()).catch(() => { /* A click retries a failed warmup. */ }); }
      catch { /* A malformed optional runtime URL remains an explicit-launch error. */ }
    }
  }

  preloadSelectedModel(source, priority = 'high') {
    const owner = this.ownerDocument || document;
    const href = new URL(source, owner.baseURI).href;
    if (this.modelPreload?.href === href) {
      if (priority === 'high') this.modelPreload.fetchPriority = 'high';
      return this.modelPreload;
    }
    this.clearModelPreload();
    this.modelPreload = preloadModel(href, owner, priority);
    return this.modelPreload;
  }

  stopRuntimeWarmup() {
    this.runtimeObserver?.disconnect();
    this.runtimeObserver = null;
    this.nearViewport = false;
    if (this.warmIntent) {
      this.removeEventListener('pointerenter', this.warmIntent);
      this.removeEventListener('focusin', this.warmIntent);
      this.warmIntent = null;
    }
  }

  clearModelPreload() {
    this.modelPreload?.remove();
    this.modelPreload = null;
  }

  runtimeSource() {
    const source = this.getAttribute('runtime-src');
    return source ? new URL(source, this.ownerDocument.baseURI).href : defaultRuntime;
  }

  disconnectedCallback() {
    this.stopRuntimeWarmup();
    this.warmedSources?.clear();
    this.runtimeWarmAttempted = false;
    this.clearModelPreload();
    this.globalEvents?.abort();
    this.globalEvents = null;
    this.resizeObserver?.disconnect();
    this.stopMotion();
    this.cancelPause();
    clearTimeout(this.loadTimeout);
    if (this.loading) {
      ++this.loadRequest;
      this.loading = false;
      this.mv?.remove();
      this.mv = null;
    }
    this.mv?.pause();
  }

  configure(config = {}) {
    this.config ||= {...DEFAULTS, presets: {...DEFAULTS.presets}};
    if (config.presets && typeof config.presets === 'object') {
      for (const [key, value] of Object.entries(config.presets)) this.config.presets[key] = {...this.config.presets[key], ...value};
    }
    for (const key of ['packingItems', 'packingCameras', 'packingBounds', 'mainHinge', 'frontSlipHinge', 'penMountPosition']) {
      if (Array.isArray(config[key])) this.config[key] = config[key];
    }
    for (const key of ['mainOpeningAngle', 'mainOpeningStart', 'frontSlipOpeningAngle', 'frontSlipOpeningStart']) {
      if (Number.isFinite(config[key])) this.config[key] = config[key];
    }
    if (Number.isFinite(config.packingDuration) && config.packingDuration > 0) this.config.packingDuration = config.packingDuration;
    this.labelIndex = -1;
    if (this.ready && !this.cameraIsCustom) this.applyCamera();
  }

  source(name, palette = this.palette) {
    const prefix = palette === 'black' ? 'black-' : '';
    return (this.getAttribute(`${prefix}${name}`) || '').trim();
  }

  paletteAvailable(palette) { return Boolean(PALETTES[palette] && this.source('unified-src', palette)); }

  bindGlobalEvents() {
    if (this.globalEvents) return;
    this.globalEvents = new AbortController();
    const options = {signal: this.globalEvents.signal};
    this.ownerDocument.addEventListener('visibilitychange', () => {
      if (this.ownerDocument.hidden) { this.stopMotion(); this.mv?.pause(); }
      else this.runMotion();
    }, options);
  }

  closeViews() { const menu = this.q?.('.bvm-views'); if (menu) menu.open = false; }

  async start() {
    if (this.loading || this.ready) return;
    this.started = true;
    await this.loadModel(this.pendingCamera);
  }

  async loadModel(camera = null) {
    const source = this.source('unified-src');
    if (!source) { this.fail('This colour is not available in 3D yet.'); return; }
    this.stopMotion();
    this.cancelPause();
    clearTimeout(this.loadTimeout);
    const request = ++this.loadRequest;
    const previous = this.ready ? this.mv : null;
    this.pendingCamera = camera;
    this.ready = false;
    this.loading = true;
    if (previous) { previous.inert = true; previous.style.pointerEvents = 'none'; previous.setAttribute('aria-hidden', 'true'); }
    else this.mv?.remove();
    this.retainedViewer?.remove();
    this.retainedViewer = previous;
    this.mv = null;
    this.q('.bvm-launch').hidden = Boolean(previous);
    this.q('[data-start]').textContent = `Loading ${PALETTES[this.palette]}…`;
    this.q('.bvm-status').textContent = `Loading ${PALETTES[this.palette]}…`;
    this.q('.bvm-shell').classList.remove('bvm-error');
    this.updateControls();
    try {
      this.stopRuntimeWarmup();
      this.preloadSelectedModel(source);
      await ensureRuntime(this.runtimeSource());
      if (request !== this.loadRequest || !this.isConnected) return;
      const viewer = this.ownerDocument.createElement('model-viewer');
      this.mv = viewer;
      if (previous) viewer.style.opacity = '0';
      const attrs = {alt: `Bellvari Messenger in ${PALETTES[this.palette]}. Drag or use arrow keys to rotate.`, 'camera-controls': '', 'touch-action': 'pan-y', 'shadow-intensity': '1', 'shadow-softness': '1', exposure: '1.05', 'environment-image': 'neutral', 'min-camera-orbit': 'auto 0deg .30m', 'max-camera-orbit': 'auto 180deg 2.4m', 'field-of-view': '32deg', 'interaction-prompt': 'none', loading: 'eager'};
      Object.entries(attrs).forEach(([key, value]) => viewer.setAttribute(key, value));
      this.createHotspot(viewer);
      viewer.addEventListener('load', () => void this.finishLoad(viewer, request, camera), {once: true});
      viewer.addEventListener('error', () => { if (request === this.loadRequest) this.fail(); });
      viewer.addEventListener('progress', event => {
        if (request === this.loadRequest && this.loading && Number.isFinite(event.detail?.totalProgress)) {
          this.q('.bvm-status').textContent = `Loading ${PALETTES[this.palette]}… ${Math.round(event.detail.totalProgress * 100)}%`;
        }
      });
      const takeCamera = () => {
        if (viewer !== this.mv) return;
        this.packingGuided = false;
        this.cameraIsCustom = true;
        ++this.cameraRequest;
      };
      viewer.addEventListener('pointerdown', takeCamera);
      viewer.addEventListener('wheel', takeCamera, {passive: true});
      viewer.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) takeCamera();
      });
      viewer.addEventListener('camera-change', () => this.updatePackingLabel());
      this.q('.bvm-stage').prepend(viewer);
      if (camera) this.commitCamera(camera, true);
      else this.applyCamera(true);
      this.loadTimeout = setTimeout(() => { if (request === this.loadRequest && this.loading) this.fail(); }, 60000);
      viewer.src = source;
    } catch { if (request === this.loadRequest) this.fail(); }
  }

  async finishLoad(viewer, request, camera) {
    if (request !== this.loadRequest || viewer !== this.mv || !this.isConnected) return;
    this.clearModelPreload();
    try {
      if (!CLIPS.every(name => viewer.availableAnimations?.includes(name)) || typeof viewer.appendAnimation !== 'function') {
        throw new Error('Model animation contract unavailable');
      }
      viewer.animationCrossfadeDuration = 0;
      viewer.animationName = 'Hold';
      viewer.timeScale = 0;
      await viewer.updateComplete;
      if (request !== this.loadRequest || viewer !== this.mv || !this.isConnected) return;
      viewer.play();
      this.ready = true;
      clearTimeout(this.loadTimeout);
      this.applyLayers();
      if (camera) this.commitCamera(camera, true);
      else this.applyCamera(true);
      // Appended layers evaluate on the runtime's render tick. Keep the previous
      // finish/poster visible until the restored opening and contents pose has
      // actually had a frame to render, including a half-open colour switch.
      await viewer.updateComplete;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (request !== this.loadRequest || viewer !== this.mv || !this.isConnected) return;
      this.loading = false;
      this.pendingCamera = null;
      this.retainedViewer?.remove();
      this.retainedViewer = null;
      viewer.style.opacity = '1';
      this.q('.bvm-launch').hidden = true;
      this.q('.bvm-status').textContent = '';
      this.updateControls();
      this.runMotion();
      this.dispatchEvent(new CustomEvent('bvmx:ready', {bubbles: true, detail: {palette: this.palette}}));
    } catch { if (request === this.loadRequest) this.fail(); }
  }

  fail(message = 'The 3D view could not load. Select Retry 3D view to try again.') {
    this.clearModelPreload();
    this.stopMotion();
    this.cancelPause();
    clearTimeout(this.loadTimeout);
    ++this.loadRequest;
    this.loading = false;
    this.ready = false;
    this.mv?.remove();
    this.mv = null;
    this.retainedViewer?.remove();
    this.retainedViewer = null;
    this.hotspot = null;
    if (!this.q) return;
    this.q('.bvm-launch').hidden = false;
    this.q('[data-start]').textContent = 'Retry 3D view';
    this.q('.bvm-status').textContent = message;
    this.q('.bvm-shell').classList.add('bvm-error');
    this.updatePoster();
    this.updateControls();
  }

  async setPalette(palette) {
    if (!this.initialized || !this.paletteAvailable(palette) || this.loading) return false;
    if (palette === this.palette) return true;
    this.closeViews();
    const camera = this.captureCamera() || this.pendingCamera;
    this.clearModelPreload();
    this.palette = palette;
    this.updatePoster();
    this.updateControls();
    this.dispatchEvent(new CustomEvent('bvmx:palette-change', {bubbles: true, detail: {palette, label: PALETTES[palette], mode: this.mode}}));
    if (this.started) await this.loadModel(camera);
    else this.warmSelectedModel();
    return true;
  }

  async setMode(mode) {
    if (!['closed', 'open', 'packing'].includes(mode) || this.loading) return;
    this.closeViews();
    this.revealStage();
    this.selectedFeature = null;
    this.stopMotion();
    this.pendingTargets = null;
    if (mode === 'packing') {
      this.contentTarget = this.contentTarget > 0 ? 0 : 1;
      this.packingGuided = this.contentTarget === 1 && !reducedMotion();
      this.view = 'packing';
      this.mode = 'packing';
      this.targets = {flap: 1, main: 1, slip: 1};
      this.cameraIsCustom = false;
    } else {
      this.packingGuided = false;
      this.mode = mode;
      this.view = mode === 'closed' ? 'front' : 'inside';
      this.requestBagPose(mode === 'closed' ? {flap: 0, main: 0, slip: 0} : {flap: 1, main: 1, slip: 0});
      this.cameraIsCustom = false;
    }
    this.q('.bvm-feature-detail').textContent = mode === 'packing' ? 'See each essential find its place.' : 'Choose a detail to take a closer look.';
    this.updateControls();
    if (!this.started || !this.mv) { this.started = true; await this.loadModel(); }
    else { if (!this.pendingTargets) this.applyCamera(); this.runMotion(); }
  }

  packingRestPoint() {
    const duration = this.config.packingDuration;
    const seconds = this.packingProgress * duration;
    const boundaries = [...new Set([0, ...this.config.packingItems.map(item => item.start), duration])].sort((a, b) => a - b);
    const exact = boundaries.find(time => Math.abs(time - seconds) < 1e-6);
    if (exact != null) return exact / duration;
    // A closing request can finish only the item already moving. During
    // unpacking it finishes removing that item, retaining the earlier ones.
    const target = this.contentTarget < this.packingProgress
      ? boundaries.filter(time => time < seconds).at(-1)
      : boundaries.find(time => time > seconds);
    return clamp((target ?? seconds) / duration);
  }

  requestBagPose(targets) {
    targets = {flap: 0, main: 0, slip: 0, ...targets};
    const closesCompartment = targets.flap === 0 || targets.main === 0 || targets.slip === 0;
    const restPoint = this.packingRestPoint();
    if (closesCompartment && Math.abs(restPoint - this.packingProgress) > 1e-7) {
      this.pendingTargets = targets;
      this.contentTarget = restPoint;
      this.targets = {flap: 1, main: 1, slip: 1};
    } else {
      this.targets = targets;
      this.pendingTargets = null;
      if (closesCompartment) this.contentTarget = this.packingProgress;
    }
  }

  async selectFeature(key) {
    const feature = FEATURES[key];
    if (!feature || this.loading) return;
    this.closeViews();
    this.revealStage();
    this.stopMotion();
    this.packingGuided = false;
    this.cameraIsCustom = false;
    this.selectedFeature = key;
    this.view = feature.view;
    this.mode = feature.main || feature.slip ? 'open' : 'closed';
    this.requestBagPose({flap: feature.flap, main: feature.main, slip: feature.slip});
    this.q('.bvm-feature-detail').textContent = feature.description;
    this.updateControls();
    if (!this.started || !this.mv) { this.started = true; await this.loadModel(); }
    else { if (!this.pendingTargets) this.applyCamera(); this.runMotion(); }
  }

  async chooseView(view) {
    if (this.loading) return;
    await this.setMode('closed');
    this.view = view;
    this.cameraIsCustom = false;
    this.applyCamera();
    this.updateControls();
  }

  cancelPause() {
    if (this.pauseFrame != null) cancelAnimationFrame(this.pauseFrame);
    this.pauseFrame = null;
  }

  applyLayers() {
    if (!this.ready || !this.mv) return;
    const viewer = this.mv;
    const options = time => ({time, timeScale: 0, weight: 1, repetitions: 1, fade: false, warp: false});
    // Global currentTime would reset every appended action. These four clips
    // target disjoint nodes and keep arbitrary contents/opening poses independent.
    viewer.appendAnimation('FlapOpening', options(clamp(this.poses.flap)));
    viewer.appendAnimation('MainOpening', options(clamp(this.poses.main)));
    viewer.appendAnimation('FrontSlipOpening', options(this.frontSlipClipTime()));
    viewer.appendAnimation('PackingContents', options(clamp(this.packingProgress) * this.config.packingDuration));
    this.cancelPause();
    const request = this.loadRequest;
    this.pauseFrame = requestAnimationFrame(() => {
      this.pauseFrame = requestAnimationFrame(() => {
        this.pauseFrame = null;
        if (!this.motionFrame && viewer === this.mv && request === this.loadRequest) viewer.pause();
      });
    });
  }

  stopMotion() {
    ++this.motionRequest;
    if (this.motionFrame != null) cancelAnimationFrame(this.motionFrame);
    this.motionFrame = null;
  }

  runMotion() {
    if (!this.ready || this.loading || !this.isConnected || this.ownerDocument.hidden || this.motionFrame != null) return;
    const operation = ++this.motionRequest;
    const viewer = this.mv;
    const request = this.loadRequest;
    let previous;
    viewer.play();
    const step = time => {
      if (operation !== this.motionRequest || request !== this.loadRequest || viewer !== this.mv || !this.isConnected) return;
      this.motionFrame = null;
      const reduce = reducedMotion();
      if (reduce) this.packingGuided = false;
      const delta = reduce ? Infinity : Math.min(.08, Math.max(0, (time - (previous ?? time)) / 1000));
      previous = time;
      const move = (from, to, amount) => from === to ? to : Math.abs(to - from) <= amount ? to : from + Math.sign(to - from) * amount;
      // Open the flap before either compartment; close both before the flap. Reversals use
      // the current continuous pose and never replace the entire animated model.
      if (this.targets.flap === 1) this.poses.flap = move(this.poses.flap, 1, delta);
      if (this.targets.main === 0 || this.poses.flap === 1) this.poses.main = move(this.poses.main, this.targets.main, delta);
      if (this.targets.slip === 0 || this.poses.flap === 1) this.poses.slip = move(this.poses.slip, this.targets.slip, delta);
      if (this.targets.flap === 0 && this.poses.main === 0 && this.poses.slip === 0) this.poses.flap = move(this.poses.flap, 0, delta);
      this.updateOpeningCamera();
      const openingsReady = this.poses.flap === 1 && this.poses.main === 1 && this.poses.slip === 1;
      const contentsBefore = this.packingProgress;
      if (openingsReady && this.packingProgress !== this.contentTarget) {
        const duration = this.contentTarget > this.packingProgress ? this.config.packingDuration : 3.2;
        this.packingProgress = move(this.packingProgress, this.contentTarget, delta / duration);
        if (this.packingGuided) this.applyPackingCamera();
      }
      if (this.packingProgress === this.contentTarget) {
        if (this.pendingTargets) {
          this.targets = this.pendingTargets; this.pendingTargets = null;
          if (!this.cameraIsCustom) this.applyCamera(reduce);
        }
        if (this.mode === 'packing' && this.contentTarget === 0) {
          this.mode = 'open'; this.view = 'inside'; this.packingGuided = false;
          this.targets = {flap: 1, main: 1, slip: 0};
          if (!this.cameraIsCustom) this.applyCamera(reduce);
        }
        if (this.mode === 'packing' && this.contentTarget === 1 && contentsBefore !== 1 && !this.cameraIsCustom) this.applyCamera(reduce);
      }
      this.applyLayers();
      this.updatePackingLabel();
      const pending = this.poses.flap !== this.targets.flap || this.poses.main !== this.targets.main || this.poses.slip !== this.targets.slip || (openingsReady && this.packingProgress !== this.contentTarget) || this.pendingTargets;
      if (pending) this.motionFrame = requestAnimationFrame(step);
      else this.updateControls();
    };
    this.motionFrame = requestAnimationFrame(step);
  }

  preset(view = this.view) { return this.config.presets[view] || this.config.presets.front; }

  captureCamera() {
    if (!this.ready || !this.mv?.getCameraOrbit) return null;
    const orbit = this.mv.getCameraOrbit(), target = this.mv.getCameraTarget();
    return {theta: orbit.theta * 180 / Math.PI, phi: orbit.phi * 180 / Math.PI, radius: orbit.radius, target: [target.x, target.y, target.z]};
  }

  fitCamera(pose, bounds) {
    if (!bounds || !this.mv) return pose;
    const canvas = this.mv.getBoundingClientRect();
    if (!canvas.width || !canvas.height) return pose;
    const theta = pose.theta * Math.PI / 180, phi = pose.phi * Math.PI / 180;
    const right = [Math.cos(theta), 0, -Math.sin(theta)];
    const up = [-Math.cos(phi) * Math.sin(theta), Math.sin(phi), -Math.cos(phi) * Math.cos(theta)];
    const out = [Math.sin(phi) * Math.sin(theta), Math.cos(phi), Math.sin(phi) * Math.cos(theta)];
    const vertical = Math.tan((this.mv.getFieldOfView?.() || 32) * Math.PI / 360);
    const horizontal = vertical * canvas.width / canvas.height;
    const dot = (a, b) => a.reduce((sum, value, index) => sum + value * b[index], 0);
    let radius = pose.radius;
    for (let corner = 0; corner < 8; corner++) {
      const v = pose.target.map((value, axis) => bounds[axis + ((corner >> axis) & 1) * 3] - value);
      const depth = dot(out, v);
      radius = Math.max(radius, depth + Math.abs(dot(right, v)) / (horizontal * .86), depth + Math.abs(dot(up, v)) / (vertical * .85));
    }
    return {...pose, radius: clamp(radius, .3, 2.4)};
  }

  commitCamera(pose, immediate = reducedMotion()) {
    if (!this.mv || !pose) return;
    const viewer = this.mv, operation = ++this.cameraRequest;
    viewer.cameraOrbit = `${pose.theta}deg ${pose.phi}deg ${pose.radius}m`;
    viewer.cameraTarget = pose.target.map(value => `${value}m`).join(' ');
    if (immediate) void Promise.resolve(viewer.updateComplete).then(() => {
      if (operation === this.cameraRequest && viewer === this.mv && this.isConnected) viewer.jumpCameraToGoal?.();
    });
  }

  applyCamera(immediate = false) {
    if (this.packingGuided && this.packingProgress > 0 && this.packingProgress < 1) { this.applyPackingCamera(); return; }
    const pose = this.view === 'packing' && this.packingProgress === 1 && this.contentTarget === 1 ? this.preset('inside') : this.preset();
    const bounds = ['front', 'back', 'carry'].includes(this.view) ? [-.22, 0, -.075, .22, .345, .10] : null;
    this.commitCamera(this.fitOpeningCamera(this.fitCamera(pose, bounds)), immediate || reducedMotion());
  }

  flapCameraEnvelope() {
    const progress = clamp(this.poses.flap);
    return smooth(clamp((progress - .1) / .15)) * smooth(clamp((.9 - progress) / .15));
  }

  fitOpeningCamera(pose) {
    const amount = this.flapCameraEnvelope();
    if (!amount) return pose;
    // The single flap rises above the body midway through either direction.
    // Preserve the chosen orbit while making room for its temporary height.
    const target = [pose.target[0], Math.max(.295, pose.target[1]), -.02];
    const raised = this.fitCamera({...pose, target}, [-.24, 0, -.25, .24, .61, .20]);
    return {...pose,
      radius: pose.radius + (Math.max(pose.radius, raised.radius) - pose.radius) * amount,
      target: pose.target.map((value, axis) => value + (target[axis] - value) * amount)
    };
  }

  updateOpeningCamera() {
    if (this.cameraIsCustom) { this.openingCameraActive = false; return; }
    const active = this.flapCameraEnvelope() > 0;
    if (active || this.openingCameraActive) this.applyCamera();
    this.openingCameraActive = active;
  }

  packingFrameBounds(seconds) {
    const bounds = this.config.packingBounds.slice();
    this.config.packingItems.forEach((item, index) => {
      const end = this.config.packingItems[index + 1]?.start ?? this.config.packingDuration;
      if (seconds < item.start - .35 || seconds >= end - .25) return;
      // Anticipate the next item gently, then retire its entry envelope as it
      // descends. Empty air above already-seated items never controls the fit.
      const arrive = smooth(clamp((seconds - item.start + .35) / .35));
      const descend = smooth(clamp((seconds - item.start - .18) / Math.max(.01, end - item.start - .43)));
      let amount = arrive * (1 - descend);
      if (seconds > this.config.packingDuration - .5) amount *= 1 - smooth(clamp((seconds - this.config.packingDuration + .5) / .5));
      bounds[4] = Math.max(bounds[4], this.config.packingBounds[4] + ((item.entryTop ?? .54) - this.config.packingBounds[4]) * amount);
      bounds[5] = Math.max(bounds[5], this.config.packingBounds[5] + ((item.entryFront ?? this.config.packingBounds[5]) - this.config.packingBounds[5]) * amount);
    });
    return bounds;
  }

  applyPackingCamera() {
    if (!this.packingGuided || !this.ready || reducedMotion()) return;
    const seconds = this.packingProgress * this.config.packingDuration;
    const keys = this.config.packingCameras;
    if (!keys.length) return;
    let index = 1;
    while (index < keys.length - 1 && seconds > keys[index][0]) index++;
    const [start, a] = keys[Math.max(0, index - 1)], [end, b] = keys[Math.min(index, keys.length - 1)];
    const progress = smooth(clamp((seconds - start) / (end - start || 1)));
    const angle = ((b.theta - a.theta + 180) % 360 + 360) % 360 - 180;
    const mix = (x, y) => x + (y - x) * progress;
    let pose = this.fitCamera({theta: a.theta + angle * progress, phi: mix(a.phi, b.phi), radius: mix(a.radius, b.radius), target: a.target.map((value, axis) => mix(value, b.target[axis]))}, this.packingFrameBounds(seconds));
    if (seconds > this.config.packingDuration - .5) {
      const finish = this.preset('inside');
      const fraction = smooth(clamp((seconds - this.config.packingDuration + .5) / .5));
      const blend = (x, y) => x + (y - x) * fraction;
      const turn = ((finish.theta - pose.theta + 180) % 360 + 360) % 360 - 180;
      pose = {theta: pose.theta + turn * fraction, phi: blend(pose.phi, finish.phi), radius: blend(pose.radius, finish.radius), target: pose.target.map((value, axis) => blend(value, finish.target[axis]))};
    }
    // The final view is the authored interior close-up. Refitting its enclosing
    // box would reintroduce empty corners and push the finished bag away again.
    this.commitCamera(pose, true);
  }

  createHotspot(viewer) {
    const hotspot = this.ownerDocument.createElement('div');
    hotspot.className = 'bvm-packing-hotspot';
    hotspot.setAttribute('slot', 'hotspot-packing-target');
    hotspot.setAttribute('data-position', '0m .25m 0m');
    hotspot.setAttribute('data-normal', '0 1 1');
    hotspot.setAttribute('aria-hidden', 'true');
    hotspot.hidden = true;
    hotspot.innerHTML = '<span class="bvm-hotspot-dot"></span><span class="bvm-hotspot-caption"><span class="bvm-hotspot-item"></span><span class="bvm-hotspot-destination"></span></span>';
    viewer.append(hotspot);
    this.hotspot = hotspot;
    this.labelIndex = -1;
  }

  openingAmount(rawTime, start = this.config.mainOpeningStart) {
    return clamp((rawTime - start) / (1 - start));
  }

  frontSlipAmount() {
    // The outer face follows the main wall for clearance, while its separate
    // organiser control adds its own opening. Neither user state is overwritten.
    return (this.openingAmount(this.poses.main) + this.openingAmount(this.poses.slip, this.config.frontSlipOpeningStart)) / 2;
  }

  frontSlipClipTime() {
    const amount = this.frontSlipAmount();
    return amount ? this.config.frontSlipOpeningStart + (1 - this.config.frontSlipOpeningStart) * amount : 0;
  }

  penMountPose() {
    const pivot = this.config.penMountPosition;
    const bow = (point, time) => {
      const [x, height, front] = point;
      const across = Math.pow(Math.max(0, 1 - (x / .197) ** 2), 1.2);
      const depth = (.018 * smooth(clamp((height - .100) / .090)) + .022 * smooth(clamp((height - .235) / .050))) * this.openingAmount(time) * across;
      return [x, height - .007 * depth / .040, front + depth];
    };
    const keys = [0, 7 / 30, .5, .75, 1];
    const time = clamp(this.poses.main);
    let index = 1;
    while (index < keys.length - 1 && time > keys[index]) index++;
    const start = keys[index - 1], end = keys[index];
    const at = sample => {
      const low = bow(pivot, sample), high = bow([pivot[0], pivot[1] + .066, pivot[2]], sample);
      return {position: low, angle: Math.atan2(high[2] - low[2], high[1] - low[1])};
    };
    const a = at(start), b = at(end), amount = clamp((time - start) / (end - start));
    // The exported mount uses linear keyframe interpolation, including rotation
    // about one axis. Match those samples instead of evaluating a different curve.
    return {position: a.position.map((value, axis) => value + (b.position[axis] - value) * amount), angle: a.angle + (b.angle - a.angle) * amount};
  }

  packingAnchor(item) {
    if (!item.followsMain && !item.followsSlip && !item.followsPenMount) return item;
    // The front pockets and pen organizer travel with the front wall. Authored
    // positions are in the closed bag's glTF space; rotate them with its parent
    // instead of leaving labels attached to where the closed wall used to be.
    const hinge = this.config.mainHinge;
    const mainProgress = this.openingAmount(this.poses.main);
    const rotate = ([x, y, z], angle) => [x, y * Math.cos(angle) - z * Math.sin(angle), y * Math.sin(angle) + z * Math.cos(angle)];
    const around = (position, origin, angle) => rotate(position.map((value, axis) => value-origin[axis]),angle).map((value,axis)=>value+origin[axis]);
    let position=item.position,normal=item.normal;
    if(item.followsPenMount){
      const mount=this.penMountPose(),pivot=this.config.penMountPosition;
      position=around(position,pivot,mount.angle).map((value,axis)=>value+mount.position[axis]-pivot[axis]);
      normal=rotate(normal,mount.angle);
    }
    if(item.followsSlip){
      const angle=this.config.frontSlipOpeningAngle*Math.PI/180*this.frontSlipAmount();
      position=around(position,this.config.frontSlipHinge,angle);normal=rotate(normal,angle);
    }
    if(item.followsMain){
      const angle=this.config.mainOpeningAngle*Math.PI/180*mainProgress;
      position=around(position,hinge,angle);normal=rotate(normal,angle);
    }
    return {...item, position, normal};
  }

  updatePackingLabel() {
    if (!this.q) return;
    const callout = this.q('.bvm-packing-callout');
    const active = this.ready && !this.loading && (this.mode === 'packing' || this.pendingTargets) && this.poses.flap === 1 && this.poses.main === 1 && this.poses.slip === 1 && this.contentTarget > this.packingProgress && this.packingProgress > 0 && this.packingProgress < 1;
    callout.hidden = !active;
    if (this.hotspot) this.hotspot.hidden = true;
    callout.classList.remove('bvm-caption-anchored');
    if (!active) return;
    const seconds = this.packingProgress * this.config.packingDuration;
    let index = 0;
    while (index < this.config.packingItems.length - 1 && seconds >= this.config.packingItems[index + 1].start) index++;
    const entry = this.config.packingItems[index];
    if (!entry) return;
    const item = this.packingAnchor(entry);
    if (index !== this.labelIndex) {
      this.labelIndex = index;
      this.q('.bvm-packing-item').textContent = item.label;
      this.q('.bvm-packing-destination').textContent = item.destination;
      if (this.hotspot) {
        this.hotspot.querySelector('.bvm-hotspot-item').textContent = item.label;
        this.hotspot.querySelector('.bvm-hotspot-destination').textContent = item.destination;
        this.mv.updateHotspot?.({name: 'hotspot-packing-target', position: item.position.map(value => `${value}m`).join(' '), normal: item.normal.join(' ')});
      }
    }
    const projection = this.mv.queryHotspot?.('hotspot-packing-target');
    const point = projection?.canvasPosition, canvas = this.mv.getBoundingClientRect();
    if (!this.hotspot || !point || !projection.facingCamera || point.z > 1 || point.z < -1 || point.x < 88 || point.x > canvas.width - 88 || point.y < 90 || point.y > canvas.height - 14) return;
    // Use the public hit-test before attaching a caption through a closed wall.
    const hit = this.mv.positionAndNormalFromPoint?.(point.x, point.y);
    const camera = this.captureCamera();
    if (hit?.position && camera) {
      const theta = camera.theta * Math.PI / 180, phi = camera.phi * Math.PI / 180;
      const position = [Math.sin(phi) * Math.sin(theta), Math.cos(phi), Math.sin(phi) * Math.cos(theta)].map((value, axis) => camera.target[axis] + value * camera.radius);
      const distance = target => Math.hypot(...position.map((value, axis) => value - target[axis]));
      if (distance([hit.position.x, hit.position.y, hit.position.z]) < distance(item.position) - .015) return;
    }
    this.hotspot.hidden = false;
    callout.classList.add('bvm-caption-anchored');
  }

  updatePoster() {
    if (!this.q) return;
    const poster = this.source('poster');
    const image = this.q('.bvm-launch img');
    if (poster) image.src = poster;
    else image.removeAttribute('src');
    image.hidden = !poster;
    const mobile = this.q('.bvm-launch source');
    const mobilePoster = this.source('poster-mobile');
    if (mobilePoster) mobile.srcset = mobilePoster; else mobile.removeAttribute('srcset');
    image.alt = `Bellvari Messenger in ${PALETTES[this.palette]}. Illustrated preview.`;
  }



  updateControls() {
    if (!this.q) return;
    this.q('.bvm-illustration').hidden = this.mode !== 'packing';
    this.q('.bvm-stage').setAttribute('aria-busy', String(this.loading));
    this.qa('[data-palette]').forEach(button => {
      button.disabled = this.loading || !this.paletteAvailable(button.dataset.palette);
      button.setAttribute('aria-pressed', String(button.dataset.palette === this.palette));
    });
    this.qa('[data-mode]').forEach(button => {
      button.disabled = this.loading || !this.paletteAvailable(this.palette);
      const mode = button.dataset.mode;
      const selected = mode === 'packing' ? this.contentTarget > 0 : this.mode === mode && !(this.selectedFeature === 'pockets');
      button.setAttribute('aria-pressed', String(selected));
      if (mode === 'packing') button.textContent = this.contentTarget > 0 ? 'Unpack' : 'Pack the bag';
    });
    this.qa('[data-feature]').forEach(button => { button.disabled = this.loading || !this.paletteAvailable(this.palette); button.setAttribute('aria-pressed', String(button.dataset.feature === this.selectedFeature)); });
    this.qa('[data-view]').forEach(button => { button.disabled = this.loading; button.setAttribute('aria-pressed', String(button.dataset.view === this.view)); });
    this.qa('[data-tool]').forEach(button => { button.disabled = button.dataset.tool !== 'fullscreen' && (!this.ready || this.loading); });
    this.q('[data-start]').disabled = this.loading || !this.paletteAvailable(this.palette);
    this.updatePackingLabel();
  }

  revealStage() {
    // Only explicit control actions call this. Model loads, colour switches and
    // every animation/camera frame preserve the shopper's document position.
    const stage = this.q('.bvm-stage');
    if (this.ownerDocument.fullscreenElement) return;
    const box = stage.getBoundingClientRect();
    const topMargin = parseFloat(getComputedStyle(stage).scrollMarginTop) || 96;
    const height = window.visualViewport?.height || window.innerHeight;
    if (box.top >= topMargin && box.bottom <= height - 12) return;
    if (box.top >= topMargin && box.top < height * .42 && box.height > height - topMargin - 12) return;
    const target = Math.max(0, window.scrollY + box.top - topMargin);
    window.scrollTo({top: target, behavior: reducedMotion() ? 'auto' : 'smooth'});
  }

  tool(action) {
    if (action === 'fullscreen') {
      this.closeViews();
      const operation = this.ownerDocument.fullscreenElement ? this.ownerDocument.exitFullscreen?.() : this.q('.bvm-shell').requestFullscreen?.();
      if (operation?.catch) operation.catch(() => { this.q('.bvm-status').textContent = 'Fullscreen is unavailable in this browser.'; });
      return;
    }
    if (!this.ready) return;
    this.packingGuided = false;
    if (action === 'reset') { this.cameraIsCustom = false; this.applyCamera(); return; }
    const pose = this.captureCamera();
    this.cameraIsCustom = true;
    this.commitCamera({...pose, radius: clamp(pose.radius * (action === 'in' ? .82 : 1.2), .3, 2.4)});
  }
}

if (!customElements.get('bv-messenger-explorer')) customElements.define('bv-messenger-explorer', BellvariMessengerExplorer);
